import { definedNotNull } from './defined/index.js';

// JavaScript Map's use object reference for comparison, instead of the object data.
//
// This means that two identically constructed keys will have different object references,
// and thus map to different values in a standard JavaScript Map.
//
// This class gets around this issue by bundling an extra map from string to K.
// These strings are JSON stringified keys.
// Using JavaScript strings as map keys will compare data, instead of references,
// allowing us to map to the same value using distinct but data-identical objects.
//
// This extra map yield object reference, which can then be used within the real underlying
// map from K to V.
//
// Look at the get(), set(), and delete() implementations for practical examples of
// how we get around the object reference issue.
export class ObjectMap<K, V> implements Map<K, V> {
  private _keyMap: Map<string, K>;
  private _objectMap: Map<K, V>;

  // Makes it so that stringified ObjectMaps can be used with their decoder again
  toJSON(): Record<string, V> {
    const object: Record<string, V> = {};

    for (const [key, value] of this._objectMap) {
      object[JSON.stringify(key)] = value;
    }

    return object;
  }

  constructor(entries?: readonly (readonly [K, V])[] | null) {
    // Create a temporary map (stringified key => [key, value]):
    // This will make ObjectMap behave the same way a Map would when duplicate keys occur:
    // If [keyA, valueA] and [keyB, valueB] are in conflict, we choose the later [keyB, valueB] pair.
    const filteredOnStringifiedKey = new Map(
      entries?.map(([K, V]) => {
        return [JSON.stringify(K), [K, V] satisfies [K, V]];
      }),
    );
    // Create key and object maps from the temporary map
    this._keyMap = new Map(
      [...filteredOnStringifiedKey.entries()].map(([stringKey, [key]]) => [stringKey, key]),
    );
    this._objectMap = new Map(
      [...filteredOnStringifiedKey.entries()].map(([, [key, value]]) => [key, value]),
    );
  }

  get(key: K): V | undefined {
    // Retrieve object reference, if key already exists.
    const _key = this._keyMap.get(JSON.stringify(key));
    return definedNotNull(_key) ? this._objectMap.get(_key) : undefined;
  }

  has(key: K): boolean {
    return this._keyMap.has(JSON.stringify(key));
  }

  set(key: K, value: V): this {
    // Retrieve object reference, if key already exists.
    const _key = this._keyMap.get(JSON.stringify(key));
    if (definedNotNull(_key)) {
      // Update value on existing object reference
      this._objectMap.set(_key, value);
    } else {
      // Insert new object to use as reference
      this._keyMap.set(JSON.stringify(key), key);
      this._objectMap.set(key, value);
    }

    return this;
  }

  delete(key: K): boolean {
    const stringifiedKey = JSON.stringify(key);
    const _key = this._keyMap.get(stringifiedKey);
    if (definedNotNull(_key)) {
      const keyRemoved = this._keyMap.delete(stringifiedKey);
      const objectRemoved = this._objectMap.delete(_key);
      return keyRemoved && objectRemoved;
    }
    return false;
  }

  merge(other: ObjectMap<K, V>) {
    return [...this._objectMap].reduce(
      (previous, [key, data]) => {
        const otherData = previous.get(key);
        if (definedNotNull(otherData)) {
          previous = previous.set(key, otherData);
        } else {
          previous = previous.set(key, data);
        }

        return previous;
      },
      new ObjectMap([...other]),
    );
  }

  clear(): void {
    this._keyMap.clear();
    this._objectMap.clear();
  }

  forEach(callbackfn: (value: V, key: K, map: Map<K, V>) => void, thisArg?: any): void {
    return this._objectMap.forEach(callbackfn, thisArg);
  }

  get size(): number {
    return this._keyMap.size;
  }

  entries(): MapIterator<[K, V]> {
    return this._objectMap.entries();
  }

  keys(): MapIterator<K> {
    return this._objectMap.keys();
  }

  values(): MapIterator<V> {
    return this._objectMap.values();
  }

  toMap(): Map<string, V> {
    return new Map(
      [...this._objectMap.entries()].map(([key, value]) => [JSON.stringify(key), value]),
    );
  }

  [Symbol.iterator](): MapIterator<[K, V]> {
    return this._objectMap.entries();
  }

  [Symbol.toStringTag]!: string;
}

export class ObjectSet<K> implements Set<K> {
  private _objectMap: ObjectMap<K, null>;

  constructor(entries?: readonly K[] | null) {
    this._objectMap = new ObjectMap(
      entries?.map((K) => {
        return [K, null];
      }),
    );
  }

  add(value: K): this {
    this._objectMap.set(value, null);
    return this;
  }
  clear(): void {
    this._objectMap.clear();
  }
  delete(value: K): boolean {
    return this._objectMap.delete(value);
  }
  forEach(callbackfn: (value: K, value2: K, set: Set<K>) => void, thisArg?: any): void {
    return new Set([...this._objectMap.keys()]).forEach(callbackfn, thisArg);
  }
  has(value: K): boolean {
    return this._objectMap.has(value);
  }
  get size(): number {
    return this._objectMap.size;
  }
  entries(): SetIterator<[K, K]> {
    return new Set([...this._objectMap.keys()]).entries();
  }
  keys(): SetIterator<K> {
    return new Set([...this._objectMap.keys()]).keys();
  }
  values(): SetIterator<K> {
    return new Set([...this._objectMap.keys()]).values();
  }
  [Symbol.iterator](): SetIterator<K> {
    return this.keys();
  }
  [Symbol.toStringTag]!: string;
}
